import React, { useState, useEffect, useRef } from "react";
import "./AdminDashboard.css";
import { auth, db } from "./firebase";
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from "firebase/auth";
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  addDoc,
  updateDoc,
  setDoc,
  serverTimestamp,
  increment,
} from "firebase/firestore";

const ADMIN_EMAIL = "systems@mrtruck.co.ke";

function formatTime(ts) {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  return d.toLocaleDateString();
}

function formatMsgTime(ts) {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function getInitial(name = "") {
  return name.trim().charAt(0).toUpperCase() || "?";
}

// ── LOGIN SCREEN ─────────────────────────────────────────
function AdminLogin({ onLogin }) {
  const [email, setEmail]       = useState(ADMIN_EMAIL);
  const [password, setPassword] = useState("");
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      setError(
        err.code === "auth/wrong-password" || err.code === "auth/user-not-found"
          ? "Invalid email or password."
          : "Login failed. Please try again."
      );
    }
    setLoading(false);
  };

  return (
    <div className="admin-login">
      <div className="admin-login-card">
        <div className="admin-login-logo">
          MR<span>TRUCK</span>
        </div>
        <div className="admin-login-sub">Support Dashboard</div>

        <h2>Admin Sign In</h2>

        <form onSubmit={handleSubmit}>
          <div className="admin-form-group">
            <label>Email</label>
            <input
              className="admin-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
            />
          </div>

          <div className="admin-form-group">
            <label>Password</label>
            <input
              className="admin-input"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          {error && <div className="admin-login-error">{error}</div>}

          <button type="submit" className="admin-login-btn" disabled={loading}>
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── MAIN DASHBOARD ────────────────────────────────────────
function Dashboard({ user }) {
  const [chats, setChats]             = useState([]);
  const [filter, setFilter]           = useState("open"); // 'all' | 'open' | 'closed'
  const [selectedId, setSelectedId]   = useState(null);
  const [messages, setMessages]       = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [replyText, setReplyText]     = useState("");
  const [sending, setSending]         = useState(false);
  const [isOnline, setIsOnline]       = useState(true);
  const messagesEndRef                = useRef(null);
  const textareaRef                   = useRef(null);

  // ── Admin presence: stored in onlineStatuses/{uid} ───────────────────
  useEffect(() => {
    const presenceRef = doc(db, "onlineStatuses", user.uid);
    setDoc(presenceRef, { isOnline: true, role: "admin", email: user.email }, { merge: true }).catch(() => {});

    const unsub = onSnapshot(presenceRef, (snap) => {
      if (snap.exists()) setIsOnline(snap.data().isOnline === true);
    });

    return () => {
      unsub();
      setDoc(presenceRef, { isOnline: false }, { merge: true }).catch(() => {});
    };
  }, [user.uid, user.email]);

  const togglePresence = () => {
    const next = !isOnline;
    setIsOnline(next);
    setDoc(doc(db, "onlineStatuses", user.uid), { isOnline: next }, { merge: true }).catch(() => {});
  };

  // ── Subscribe to all chats ────────────────────────────
  useEffect(() => {
    const q = query(
      collection(db, "chats"),
      orderBy("lastMessageAt", "desc")
    );
    const unsub = onSnapshot(q, (snap) => {
      setChats(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, []);

  // ── Subscribe to selected chat messages ───────────────
  useEffect(() => {
    if (!selectedId) { setMessages([]); return; }
    setLoadingMsgs(true);

    const q = query(
      collection(db, "chats", selectedId, "messages"),
      orderBy("createdAt", "asc")
    );
    const unsub = onSnapshot(q, (snap) => {
      setMessages(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoadingMsgs(false);
    });

    // Mark adminUnread = 0 when opening chat
    updateDoc(doc(db, "chats", selectedId), { adminUnread: 0 }).catch(() => {});

    return unsub;
  }, [selectedId]);

  // ── Auto-scroll ───────────────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // ── Send reply ────────────────────────────────────────
  const sendReply = async () => {
    const text = replyText.trim();
    if (!text || sending || !selectedId) return;
    setSending(true);
    setReplyText("");

    try {
      await addDoc(collection(db, "chats", selectedId, "messages"), {
        text,
        sender: "admin",
        createdAt: serverTimestamp(),
      });

      await updateDoc(doc(db, "chats", selectedId), {
        lastMessage: text,
        lastMessageAt: serverTimestamp(),
        customerUnread: increment(1),
        adminUnread: 0,
      });
    } catch (err) {
      console.error("Reply failed:", err);
      setReplyText(text);
    }
    setSending(false);
    textareaRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendReply();
    }
  };

  // ── Toggle chat status ─────────────────────────────────
  const toggleStatus = async (newStatus) => {
    if (!selectedId) return;
    await updateDoc(doc(db, "chats", selectedId), { status: newStatus });
  };

  const filteredChats = chats.filter((c) => {
    if (filter === "all") return true;
    return c.status === filter;
  });

  const selectedChat = chats.find((c) => c.id === selectedId);

  return (
    <div className="admin">
      {/* ── SIDEBAR ── */}
      <aside className="admin-sidebar">
        <div className="admin-sidebar-header">
          <div className="admin-sidebar-brand">
            MR<span>TRUCK</span> Support
          </div>

          <div className="admin-sidebar-filters">
            {["open", "closed", "all"].map((f) => (
              <button
                key={f}
                className={`admin-filter-btn${filter === f ? " admin-filter-btn--active" : ""}`}
                onClick={() => setFilter(f)}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="admin-chat-list">
          {filteredChats.length === 0 ? (
            <div className="admin-empty-state">
              No {filter !== "all" ? filter : ""} chats yet
            </div>
          ) : (
            filteredChats.map((chat) => (
              <div
                key={chat.id}
                className={`admin-chat-item${selectedId === chat.id ? " admin-chat-item--active" : ""}`}
                onClick={() => setSelectedId(chat.id)}
              >
                <div className="admin-chat-item-row">
                  <span className="admin-chat-name">
                    {chat.customerName || "Anonymous"}
                  </span>
                  <span className="admin-chat-time">
                    {formatTime(chat.lastMessageAt)}
                  </span>
                </div>

                <div className="admin-chat-item-row" style={{ marginBottom: 0 }}>
                  <span className="admin-chat-preview">
                    {chat.lastMessage || "—"}
                  </span>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px", flexShrink: 0 }}>
                    <span className={`admin-status-pill admin-status-pill--${chat.status || "open"}`}>
                      {chat.status === "closed" ? "Closed" : "Open"}
                    </span>
                    {(chat.adminUnread > 0) && (
                      <span className="admin-unread-badge">
                        {chat.adminUnread > 9 ? "9+" : chat.adminUnread}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="admin-sidebar-footer">
          <div className="admin-presence-row">
            <div className="admin-presence-label">
              <span className={`admin-presence-dot admin-presence-dot--${isOnline ? "online" : "offline"}`} />
              {isOnline ? "Online" : "Offline"}
            </div>
            <label className="admin-toggle" aria-label="Toggle online status">
              <input type="checkbox" checked={isOnline} onChange={togglePresence} />
              <span className="admin-toggle-track" />
              <span className="admin-toggle-thumb" />
            </label>
          </div>
          <button
            className="admin-logout-btn"
            onClick={() => signOut(auth)}
          >
            Sign Out — {user.email}
          </button>
        </div>
      </aside>

      {/* ── MAIN PANEL ── */}
      <main className="admin-main">
        {!selectedId ? (
          <div className="admin-no-selection">
            <div className="admin-no-selection-icon">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
            </div>
            <div className="admin-no-selection-text">Select a chat to view the conversation</div>
          </div>
        ) : (
          <>
            {/* Thread header */}
            <div className="admin-thread-header">
              <div className="admin-thread-customer">
                <div className="admin-thread-avatar">
                  {getInitial(selectedChat?.customerName)}
                </div>
                <div>
                  <div className="admin-thread-name">
                    {selectedChat?.customerName || "Anonymous"}
                  </div>
                  <div className="admin-thread-email">
                    {selectedChat?.customerEmail || "No email provided"}
                  </div>
                </div>
              </div>

              <div className="admin-thread-actions">
                <span className={`admin-status-pill admin-status-pill--${selectedChat?.status || "open"}`}>
                  {selectedChat?.status === "closed" ? "● Closed" : "● Open"}
                </span>

                {selectedChat?.status !== "closed" ? (
                  <button
                    className="admin-action-btn admin-action-btn--close-chat"
                    onClick={() => toggleStatus("closed")}
                  >
                    Close Chat
                  </button>
                ) : (
                  <button
                    className="admin-action-btn admin-action-btn--reopen"
                    onClick={() => toggleStatus("open")}
                  >
                    Reopen
                  </button>
                )}
              </div>
            </div>

            {/* Messages */}
            <div className="admin-messages">
              {loadingMsgs ? (
                <div className="admin-loading-msgs">Loading messages…</div>
              ) : (
                messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`admin-msg admin-msg--${msg.sender}`}
                  >
                    <span className="admin-msg-label">
                      {msg.sender === "admin" ? "You (Support)" : selectedChat?.customerName || "Customer"}
                    </span>
                    <div className={`admin-bubble admin-bubble--${msg.sender}`}>
                      {msg.text}
                    </div>
                    <span className="admin-msg-time">
                      {formatMsgTime(msg.createdAt)}
                    </span>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Reply bar */}
            {selectedChat?.status === "closed" ? (
              <div className="admin-closed-notice">
                This chat is closed.{" "}
                <button
                  style={{ background: "none", border: "none", color: "var(--brand)", cursor: "pointer", fontWeight: 600, fontSize: 13 }}
                  onClick={() => toggleStatus("open")}
                >
                  Reopen to reply
                </button>
              </div>
            ) : (
              <div className="admin-reply-bar">
                <textarea
                  ref={textareaRef}
                  className="admin-reply-textarea"
                  placeholder="Type your reply… (Enter to send, Shift+Enter for new line)"
                  rows={2}
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
                <button
                  className="admin-reply-btn"
                  onClick={sendReply}
                  disabled={!replyText.trim() || sending}
                >
                  {sending ? "Sending…" : "Send Reply"}
                </button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

// ── ROOT EXPORT ───────────────────────────────────────────
export default function AdminDashboard() {
  const [user, setUser] = useState(undefined); // undefined = loading

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUser(u));
    return unsub;
  }, []);

  if (user === undefined) {
    // Auth state loading
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--off-white)" }}>
        <span style={{ color: "var(--gray-500)", fontSize: 15 }}>Loading…</span>
      </div>
    );
  }

  if (!user) return <AdminLogin />;

  return <Dashboard user={user} />;
}
